/**
 * 🛡️ AdminGear — owner-only Gear Catalog editor.
 *
 * Flip the `tradeable` flag on any non-soulbound item. Soulbound items
 * (e.g. red_leather) are read-only by design — never tradeable.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminGear() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  const isOwner = user && (user.role === "owner" || (user.email || "").toLowerCase() === "omnimuscle@yahoo.com");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/gear`);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load gear.");
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!isOwner) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isOwner]);

  if (authLoading) return null;
  if (!isOwner) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center text-neutral-400" data-testid="admin-gear-locked">
        🔒 Owner only.
      </div>
    );
  }
  if (!items) {
    return <div className="max-w-3xl mx-auto p-8 text-center text-neutral-500">Loading…</div>;
  }

  const toggle = async (it) => {
    if (it.soulbound) return;
    setBusy(it.item_id);
    try {
      await axios.put(`${API}/admin/gear/${it.item_id}`, { tradeable: !it.tradeable });
      toast.success(`${it.name} is now ${!it.tradeable ? "tradeable" : "locked"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
    setBusy(null);
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-8" data-testid="admin-gear-page">
      <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
        <a href="/admin" className="hover:text-white">← Admin</a>
        <span className="text-neutral-700">·</span>
        <a href="/admin/heroes" className="hover:text-white">🦸 Heroes</a>
        <span className="text-neutral-700">·</span>
        <a href="/admin/bosses" className="hover:text-white">👁️ Bosses</a>
      </div>
      <h1 className="font-display text-3xl mb-2">🛡️ Gear Catalog</h1>
      <p className="text-xs text-neutral-500 mb-6 leading-relaxed">
        Toggle the <span className="text-[#FCD34D]">Tradeable</span> flag on any non-soulbound item.
        Soulbound items (e.g. consumables) can never be flipped — that's by design to keep the economy clean.
      </p>

      <div className="space-y-2">
        {items.map((it) => (
          <div
            key={it.item_id}
            data-testid={`admin-gear-${it.item_id}`}
            className={`flex items-center gap-3 p-3 border-2 ${
              it.soulbound
                ? "border-zinc-900 bg-neutral-950 opacity-70"
                : it.tradeable
                  ? "border-emerald-900/60 bg-emerald-950/10"
                  : "border-neutral-800 bg-neutral-950"
            }`}
          >
            <span className="text-3xl shrink-0 w-10 text-center">{it.icon || "🎒"}</span>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">{it.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                {it.slot} · {it.rarity} · <span className="font-mono normal-case tracking-normal">{it.item_id}</span>
              </div>
              <div className="text-[10px] text-neutral-600 mt-0.5">source: {it.source || "—"}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {it.soulbound && (
                <span className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold px-2 py-1 border border-neutral-800">
                  🔒 Soulbound
                </span>
              )}
              <button
                onClick={() => toggle(it)}
                disabled={it.soulbound || busy === it.item_id}
                data-testid={`admin-gear-toggle-${it.item_id}`}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold transition ${
                  it.soulbound
                    ? "bg-neutral-900 text-neutral-600 cursor-not-allowed"
                    : it.tradeable
                      ? "bg-emerald-700 hover:bg-emerald-600 text-white"
                      : "bg-neutral-800 hover:bg-neutral-700 text-neutral-300"
                }`}
              >
                {it.soulbound ? "Locked" : it.tradeable ? "✓ Tradeable" : "Locked"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
